import { mdiLoading } from '@mdi/js';
import Icon from '@mdi/react';
import { AnimatePresence, motion } from 'framer-motion';
import iziToast from 'izitoast';
import { MessageSquare, PenIcon, Text, Users } from 'lucide-react';
import { useContext, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslate } from 'sbx-crm-menu-ui';
import ButtonComponent from 'src/components/shared/ButtonComponent';
import Permission from 'src/components/shared/Permission';
import RolesComponent from 'src/components/shared/RolesComponent';
import { AppAgentContext } from 'src/context/ContextProvider';
import { uploadFileCompressedService } from 'src/services/AssetService';
import { appActions } from 'src/store';
import { AiAgentV3 } from 'src/types/AiAgentV3';
import { ModalType } from 'src/types/Modal';
import { AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { grantPermission } from 'src/utils';
import { defaultColorsBotV3, defaultSizesBotV3 } from 'src/utils/botGenerator/formatCss';
import { defaultValuesTextBotV3 } from 'src/utils/botGenerator/formatScript2';
import RenderFormFieldsComponent, {
    GroupTypes,
} from '../../../shared/renderFormFieldsComponent/RenderFormFieldsComponent';
import { Card } from '../../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { iconBotV3, logo_placeholder } from '../AgentLayoutComponentV2';
import { AgentSetupContextV3 } from '../AgentSetupV3Component';
import { OptionsAddComponent } from 'src/components/shared/OptionsAddComponent';

function formatFields(
    id: string,
    allowEdit: boolean,
    agent: (val: string) => string,
    objectKeys: Record<string, string>,
    type: 'text' | 'color' | 'color_transparency',
    oldExtraConfig: Record<string, any>,
    omitKeys?: string[]
) {
    // console.log(Object.keys(objectKeys))
    return Object.keys(objectKeys)
        .filter((val) => !omitKeys?.some((om) => om === val))
        .map((key) => ({
            name: `look_and_feel_chat.extra_config.${key}`,
            label: agent(`${key}_label`),
            placeholder: agent(`${key}_label`),
            classNameLabel,
            type,
            classNameInput,
            oldValue: oldExtraConfig[key] || objectKeys[key],
            disabled: id !== 'create' && !allowEdit,
        }));
}

const classNameLabel = 'text-purple-700 font-semibold text-[14px]';
const classNameLabelAgent = '!text-purple-700 !font-semibold';
const classNameInput =
    '!border-purple-300 !focus:border-purple-500 !focus:ring !focus:ring-purple-200 !focus:ring-opacity-50';
const AgentIdentityComponent = () => {
    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');
    const { control, setValue, getValues, oldExtraConfig, watch } = useContext(AgentSetupContextV3);
    const [isLoading, setIsLoading] = useState(false);
    const inputAvatarRef = useRef<HTMLInputElement | null>(null);
    const inputLogoRef = useRef<HTMLInputElement | null>(null);
    const {
        state: {
            session: { permissions, ai_agent_config },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const { id } = useParams();

    const [activeSection, setActiveSection] = useState('chat');

    const allowEdit = useMemo(() => {
        return grantPermission(AI_AGENT_V2_EDIT, permissions);
    }, [permissions]);

    const srcImage = useMemo(() => {
        const avatar = getValues().avatar;
        if (avatar) {
            return avatar;
        } else return iconBotV3;
    }, [getValues().avatar]);

    const onChangeAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const files = [e.target.files[0]];
            setIsLoading(true);
            const res = await uploadFileCompressedService(ai_agent_config.folder_key_avatars, files);

            if (res.success) {
                const name = res.items[0].name;
                setValue('avatar', `${ai_agent_config.path_avatars}/${name}`);
                iziToast.success({
                    message: common('image_uploaded'),
                    position: 'topCenter',
                });
                setIsLoading(false);
            } else {
                iziToast.warning({
                    message: common('error_uploading_image'),
                    position: 'topCenter',
                });
                setIsLoading(false);
            }
        }
    };

    const identityFields = useMemo((): GroupTypes<keyof AiAgentV3> => {
        return {
            agent1: {
                columns: 'grid-cols-2 mb-3',
                items: [
                    {
                        name: 'name',
                        label: agent('project_name'),
                        classNameLabel: classNameLabelAgent,
                        classNameInput,
                        placeholder: agent('project_name'),
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'look_and_feel_chat.agent_name',
                        label: agent('agent_name'),
                        placeholder: agent('agent_name'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
            size_toggle: {
                columns: 'grid-cols-1 items-center',
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.fbw_bot_avatar_scale',
                        label: agent('avatar_size_label'),
                        classNameLabel: classNameLabelAgent,
                        type: 'range',
                        min: 0,
                        max: 300,
                        unit: '%',
                        oldValue: oldExtraConfig['fbw_bot_avatar_scale'] ?? defaultSizesBotV3.fbw_bot_avatar_scale,
                        placeholder: agent('avatar_size_label'),
                        disabled: id !== 'create' && !allowEdit,
                    },
                ],
            },
            size_header: {
                columns: 'lg:grid-cols-[30%_70%]',
                label: agent('header'),
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.fbw_bot_logo_header_scale',
                        label: agent('header_logo_width_label'),
                        type: 'range',
                        min: 0,
                        max: 200,
                        unit: '%',
                        oldValue:
                            oldExtraConfig['fbw_bot_logo_header_scale'] ?? defaultSizesBotV3.fbw_bot_logo_header_scale,
                        placeholder: agent('header_logo_width_label'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                    },
                ],
            },
            chat: {
                columns: 'grid-cols-1 lg:grid-cols-2 mb-3',
                items: [
                    // {
                    //     name: 'look_and_feel_chat.suggested_message',
                    //     label: agent('suggested_message'),
                    //     placeholder: agent('suggested_message'),
                    //     type: 'select',
                    //     classNameLabel,
                    //     classNameInput,
                    //     isMulti: true,
                    //     options: [],
                    //     disabled: id !== 'create' && !allowEdit,
                    //     rules: {
                    //         required: false,
                    //     },
                    // },
                    {
                        name: 'look_and_feel_chat.privacy_policy_url',
                        label: agent('privacy_policy_url'),
                        placeholder: agent('privacy_policy_url'),
                        classNameLabel,
                        classNameInput,
                        type: 'url',
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                ],
            },
            // styles_bot: {
            //     columns: 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 items-center mb-3',
            //     items: formatFields(
            //         id || '',
            //         allowEdit,
            //         agent,
            //         defaultColorsBotV3,
            //         'color_transparency',
            //         oldExtraConfig
            //     ),
            // },
            styles_bot2: {
                columns: 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 items-center mb-3',
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.fbw_bot_panel_radius',
                        label: agent('fbw_bot_panel_radius_label'),
                        type: 'range',
                        min: 0,
                        max: 40,
                        unit: 'px',
                        oldValue: oldExtraConfig['fbw_bot_panel_radius'] ?? defaultSizesBotV3.fbw_bot_panel_radius,
                        placeholder: agent('fbw_bot_panel_radius_label'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.fbw_bot_panel_border_size',
                        label: agent('fbw_bot_panel_border_size_label'),
                        type: 'range',
                        min: 0,
                        max: 10,
                        unit: 'px',
                        oldValue:
                            oldExtraConfig['fbw_bot_panel_border_size'] ?? defaultSizesBotV3.fbw_bot_panel_border_size,
                        placeholder: agent('fbw_bot_panel_border_size_label'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                    },
                    {
                        name: 'look_and_feel_chat.extra_config.fbw_logo_header_radius',
                        label: agent('fbw_logo_header_radius_label'),
                        type: 'range',
                        min: 0,
                        max: 50,
                        unit: '%',
                        oldValue: oldExtraConfig['fbw_logo_header_radius'] ?? defaultSizesBotV3.fbw_logo_header_radius,
                        placeholder: agent('fbw_logo_header_radius_label'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                    },
                ],
            },
            text_format_text_bubble: {
                columns: 'grid-cols-1 mb-3',
                label: agent('text_bubble'),
                items: [
                    {
                        name: 'look_and_feel_chat.extra_config.closed_welcome',
                        label: agent('text_bubble'),
                        placeholder: agent('text_bubble'),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            max: 30,
                        },
                    },
                ],
            },
            text_format_form_chat: {
                columns: 'grid-cols-2 mb-3',
                label: agent('form_chat'),
                items: [
                    {
                        name: `look_and_feel_chat.extra_config.initial_bot_message`,
                        label: agent(`initial_bot_message_label`),
                        placeholder: agent(`initial_bot_message_label`),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                    },
                    ...formatFields(id || '', allowEdit, agent, defaultValuesTextBotV3 as any, 'text', oldExtraConfig, [
                        // 'suggestions',
                        'closed_welcome',
                        'description',
                    ]),
                ],
            },
            roles_messages: {
                columns: 'grid-cols-2 mb-3',
                label: agent('roles_messages'),
                items: [
                    {
                        name: `look_and_feel_chat.extra_config.role_message`,
                        label: agent(`role_message`),
                        placeholder: agent(`role_message`),
                        classNameLabel,
                        classNameInput,
                        disabled: id !== 'create' && !allowEdit,
                    },
                ],
            },
        };
    }, [oldExtraConfig]);

    const subTabs = useMemo(() => {
        return [
            { id: 'chat', icon: MessageSquare, label: agent('look_and_feel_chat'), color: 'from-pink-500 to-rose-500' },
            { id: 'text_format', icon: Text, label: agent('text_format'), color: 'from-purple-500 to-pink-500' },
            {
                id: 'roles',
                icon: Users,
                label: 'Roles',
                color: 'from-purple-500 to-pink-500',
            },
        ];
    }, []);

    const onChangeLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const files = [e.target.files[0]];
            setIsLoading(true);
            const res = await uploadFileCompressedService(ai_agent_config.folder_key_avatars, files);

            if (res.success) {
                const name = res.items[0].name;
                setValue('avatar', `${ai_agent_config.path_avatars}/${name}`);
                iziToast.success({
                    message: common('image_uploaded'),
                    position: 'topCenter',
                });
                setIsLoading(false);
            } else {
                iziToast.warning({
                    message: common('error_uploading_image'),
                    position: 'topCenter',
                });
                setIsLoading(false);
            }
        }
    };

    const openAssetsModal = (key: string) => {
        dispatch(
            appActions.openModal({
                type: ModalType.ASSETS_AGENT,
                data: {
                    title: common('select_image'),
                    setData: (item) => setValue(key as any, item),
                    onClose: () => dispatch(appActions.closeModal(ModalType.ASSETS_AGENT)),
                },
            })
        );
    };

    const srcImageLogo = useMemo(() => {
        const logo = getValues().look_and_feel_chat.extra_config?.logo_header;
        if (logo) {
            return logo;
        } else return logo_placeholder;
    }, [getValues().look_and_feel_chat.extra_config?.logo_header]);

    return (
        <div className="py-6">
            <Card className="bg-gradient-to-br from-pink-100 to-purple-100 shadow-xl">
                {/* <CardHeader>
                    <CardTitle className="text-2xl font-bold text-purple-800">{agent('identity_settings')}</CardTitle>
                </CardHeader> */}

                <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full p-4">
                    <TabsList className="grid w-full grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 rounded-xl bg-purple-100 mb-36 md:mb-12 xl:mb-16">
                        {subTabs.map((option, index) => (
                            <TabsTrigger
                                key={index}
                                value={option.id}
                                className={`
                        flex items-center justify-center py-2 px-1
                        rounded-lg transition-all duration-300
                        data-[state=active]:bg-gradient-to-br ${option.color}
                        data-[state=active]:text-white data-[state=active]:shadow-lg
                        hover:bg-white hover:bg-opacity-50
                      `}
                            >
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                    className="flex items-center"
                                >
                                    <option.icon className="w-5 h-5 mr-2" />
                                    <span className="text-sm font-medium">{option.label}</span>
                                </motion.div>
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeSection}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                        >
                            <TabsContent value="chat">
                                <div className="grid grid-cols-1 gap-x-6 gap-y-3 mb-5">
                                    {identityFields['agent1'].label && identityFields['chat'].label}
                                    <div className={`grid ${identityFields['agent1'].columns ?? 'grid-cols-1 '} gap-3`}>
                                        <RenderFormFieldsComponent
                                            fields={identityFields['agent1'].items}
                                            control={control}
                                        />
                                    </div>
                                    <div className="grid lg:grid-cols-[30%_70%] mb-5">
                                        <div className="flex gap-2 justify-center items-center">
                                            <label
                                                htmlFor=""
                                                className="block text-sm  leading-6 !text-purple-700 !font-semibold"
                                            >
                                                Avatar
                                            </label>
                                            <div className="py-2 px-4 flex gap-2">
                                                <input
                                                    className="hidden"
                                                    type="file"
                                                    accept="image/png, image/jpeg"
                                                    onChange={onChangeAvatar}
                                                    ref={inputAvatarRef}
                                                />
                                                <div className="relative">
                                                    <img
                                                        src={srcImage}
                                                        alt="avatar"
                                                        width={60}
                                                        className="rounded-full"
                                                    />
                                                    <Permission permission={AI_AGENT_V2_EDIT}>
                                                        <div
                                                            className="bg-white rounded-full cursor-pointer bottom-[-6px] right-0 w-6 flex justify-center items-center shadow absolute"
                                                            onClick={() => openAssetsModal('avatar')}
                                                        >
                                                            <PenIcon width={12} />
                                                        </div>
                                                    </Permission>
                                                </div>
                                                {isLoading && <Icon className="mr-1" size={1} path={mdiLoading} spin />}
                                            </div>
                                        </div>

                                        <div
                                            className={`grid ${identityFields['size_toggle'].columns ?? 'grid-cols-1 '} gap-3`}
                                        >
                                            <RenderFormFieldsComponent
                                                fields={identityFields['size_toggle'].items}
                                                control={control}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {/* <h3 className="tracking-tight text-xl font-bold text-purple-800 mb-2">
                                    {identityFields['text_format_header'].label}
                                </h3> */}
                                <div className={`grid ${identityFields['size_header'].columns ?? 'grid-cols-1 '} mb-5`}>
                                    <div className="flex gap-2 items-center">
                                        <label
                                            htmlFor=""
                                            className="block text-sm  leading-6 !text-purple-700 !font-semibold"
                                        >
                                            {agent('logo_header_label')}
                                        </label>
                                        <div className="py-2 px-4 flex gap-2">
                                            <input
                                                className="hidden"
                                                type="file"
                                                accept="image/png, image/jpeg"
                                                onChange={onChangeLogo}
                                                ref={inputLogoRef}
                                            />
                                            <div className="relative">
                                                <img
                                                    src={srcImageLogo}
                                                    alt="logo"
                                                    width={45}
                                                    className="rounded-full"
                                                />
                                                <Permission permission={AI_AGENT_V2_EDIT}>
                                                    <div
                                                        className="bg-white rounded-full cursor-pointer bottom-[-6px] right-0 w-6 flex justify-center items-center shadow absolute"
                                                        onClick={() =>
                                                            openAssetsModal(
                                                                'look_and_feel_chat.extra_config.logo_header'
                                                            )
                                                        }
                                                    >
                                                        <PenIcon width={12} />
                                                    </div>
                                                </Permission>
                                            </div>
                                            {isLoading && <Icon className="mr-1" size={1} path={mdiLoading} spin />}
                                        </div>
                                    </div>
                                    <RenderFormFieldsComponent
                                        fields={identityFields['size_header'].items}
                                        control={control}
                                    />
                                </div>
                                <div className={`flex flex-col`}>
                                    {identityFields['chat'].label && identityFields['chat'].label}
                                    <div
                                        className={`grid ${identityFields['chat'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <OptionsAddComponent
                                            label={agent('suggested_message')}
                                            isRequired
                                            title={common('add_messages')}
                                            description={agent('message_suggested_description')}
                                            placeholder={common('type_message')}
                                            values={watch('look_and_feel_chat.suggested_message')}
                                            onChange={(items) =>
                                                setValue('look_and_feel_chat.suggested_message', items)
                                            }
                                        />
                                        <RenderFormFieldsComponent
                                            fields={identityFields['chat'].items}
                                            control={control}
                                        />
                                    </div>

                                    {/* <div
                                        className={`grid ${identityFields['styles_bot'].columns ?? 'grid-cols-1 '} gap-3 mb-6`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['styles_bot'].items}
                                            control={control}
                                        />
                                    </div> */}

                                    <div
                                        className={`grid ${identityFields['styles_bot2'].columns ?? 'grid-cols-1 '} gap-3 mb-4`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['styles_bot2'].items}
                                            control={control}
                                        />
                                    </div>

                                    <div className="flex justify-end">
                                        <ButtonComponent
                                            label={common('reset_styles')}
                                            className="primary-gradient-button"
                                            onClick={() => {
                                                setValue('look_and_feel_chat', {
                                                    ...getValues('look_and_feel_chat'),
                                                    extra_config: {
                                                        ...getValues('look_and_feel_chat.extra_config'),
                                                        ...defaultColorsBotV3,
                                                        ...defaultSizesBotV3,
                                                    },
                                                });
                                            }}
                                            disabled={id !== 'create' && !allowEdit}
                                        />
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value={'text_format'}>
                                <div className={`flex flex-col mt-28 min-[1025px]:mt-3`}>
                                    <h3 className="tracking-tight text-xl font-bold text-purple-800 mb-2">
                                        {identityFields['text_format_text_bubble'].label &&
                                            identityFields['text_format_text_bubble'].label}
                                    </h3>
                                    <div
                                        className={`grid ${identityFields['text_format_text_bubble'].columns ?? 'grid-cols-1 '} gap-3 mb-5`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['text_format_text_bubble'].items}
                                            control={control}
                                        />
                                    </div>

                                    <h3 className="tracking-tight text-xl font-bold text-purple-800 mt-3 mb-2">
                                        {identityFields['text_format_form_chat'].label &&
                                            identityFields['text_format_form_chat'].label}
                                    </h3>

                                    <div
                                        className={`grid ${identityFields['text_format_form_chat'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={identityFields['text_format_form_chat'].items}
                                            control={control}
                                        />
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value={'roles'}>
                                <h3 className="tracking-tight text-xl font-bold text-purple-800 mb-2">
                                    {identityFields['roles_messages'].label && identityFields['roles_messages'].label}
                                </h3>
                                <div
                                    className={`grid ${identityFields['roles_messages'].columns ?? 'grid-cols-1 '} gap-3 mb-5`}
                                >
                                    <RenderFormFieldsComponent
                                        fields={identityFields['roles_messages'].items}
                                        control={control}
                                    />
                                </div>
                                <RolesComponent
                                    value={watch().look_and_feel_chat?.extra_config?.roles_options || []}
                                    setValue={(val) => {
                                        setValue('look_and_feel_chat.extra_config.roles_options', val);
                                    }}
                                    disabled={id !== 'create' && !allowEdit}
                                />
                            </TabsContent>
                        </motion.div>
                    </AnimatePresence>
                </Tabs>
            </Card>
        </div>
    );
};

export default AgentIdentityComponent;
